import { isSkyward, rowLabel } from './lossStaging';
import { apiErrorText } from '../../utils/apiErrorText';

// The grid's field names, mapped onto the correction overlay's column names.
const CORRECTION_FIELD = {
  lossCause: 'loss_cause',
  description: 'description',
  incurred: 'incurred',
  expense: 'expense',
  recovery: 'recovery',
  locationId: 'location_id',
  // Sent as the yyyy-MM-dd calendar date the row already holds; the overlay stores it verbatim.
  occurredAt: 'occurred_at',
};

const DETAIL_FIELDS = [
  'id',
  'applicationId',
  'locationId',
  'carrier',
  'claimNumber',
  'status',
  'lossCause',
  'lossCauseOther',
  'description',
  'occurredAt',
  'expense',
  'incurred',
  'recovery',
];

/* A new row's blank expense or recovery cell means zero, not null — both are NOT NULL columns, and
   the CSV importer resolves the same missing inputs the same way. These are applied against `== null`
   rather than only to absent keys because agNumberCellEditor yields null, not undefined, for a cell
   the user emptied, and a new row has no snapshot entry for validation to catch the clearing against.
   incurred is deliberately absent: validation makes the user type it rather than defaulting money to
   nothing. */
const NEW_ROW_DEFAULTS = { expense: 0, recovery: 0 };

/* A LossDetail payload for the self-reported write paths: grid bookkeeping and the Skyward feed
   context are dropped. Exported so replaceLossHistory.js (Clear all / Adopt from…) shapes adopted
   rows the same way rather than inventing its own serialization. */
export const toDetailPayload = (row, applicationId) => {
  const payload = DETAIL_FIELDS.reduce((out, field) => {
    if (row[field] !== undefined) out[field] = row[field];
    return out;
  }, {});
  payload.applicationId = applicationId;
  if (row._new) {
    Object.keys(NEW_ROW_DEFAULTS).forEach((field) => {
      if (payload[field] == null) payload[field] = NEW_ROW_DEFAULTS[field];
    });
  }
  // 'other' is the marker for a free-text cause, so it is the one cause that does not replace it.
  if (payload.lossCause && payload.lossCause !== 'other') {
    payload.lossCauseOther = null;
  }
  return payload;
};

const toCorrections = (rowEdits) =>
  Object.keys(rowEdits).reduce((out, field) => {
    const name = CORRECTION_FIELD[field];
    if (name) {
      const value = rowEdits[field];
      out[name] = value == null ? '' : String(value);
    }
    return out;
  }, {});

/* Three callers in the grid share this: a per-row save failure, a failed CSV download and a failed
   post-save reload, which is why `fallback` exists — each names its own operation rather than all three
   reporting as "save failed". The parsing itself (including the HTML-error-page and 5xx guard) lives in
   utils/apiErrorText.js, shared with the customer-attachment Change dialog. */
export const saveErrorText = (error, fallback = 'save failed') => apiErrorText(error, fallback);

// A self-reported row counts as "touched" only when the resulting set actually differs from the
// server: a survivor added this session, an existing row staged for removal, or an existing row with
// an edited field. A row that is both `_new` and `_removed` was never persisted and never will be —
// it contributes nothing to the set below and must not trigger a save on its own (see the
// removed-while-new test).
const isTouchedSelfRow = (row) => {
  if (isSkyward(row) || (row._new && row._removed)) {
    return false;
  }
  return row._new || row._removed || (row._edited || []).length > 0;
};

/** Commit staged loss changes. Skyward corrections still save one claim line at a time — a claim
    line's failure is independent of the others. Self-reported rows save as a single batch: the PUT
    replaces the application's complete self-reported set, so either all of it lands or none does,
    and every currently-staged self-reported row is reported as failed together. */
export const saveLossChanges = async ({ applicationId, displayRows, suppressedRows = [], staging, api }) => {
  const failures = [];

  for (const row of displayRows) {
    if (!isSkyward(row)) {
      continue;
    }
    const key = row._key;
    const rowEdits = staging.edits[key];

    try {
      if (row._removed) {
        // The feed row is immutable; a removal is a durable exclusion in the overlay.
        await api.upsertSkywardCorrections(applicationId, row.claimLineKey, { excluded: 'true' });
      } else if (row._restored) {
        // Only the exclusion is reverted — any other correction on this claim line is left in place.
        await api.revertSkywardCorrectionField(applicationId, row.claimLineKey, 'excluded');
      } else if (row._reverted) {
        await api.revertSkywardCorrections(applicationId, row.claimLineKey);
      } else if (rowEdits && Object.keys(rowEdits).length) {
        await api.upsertSkywardCorrections(applicationId, row.claimLineKey, toCorrections(rowEdits));
      } else {
        continue;
      }
    } catch (error) {
      failures.push({ key, label: rowLabel(row), error });
    }
  }

  const touchedSelfRows = displayRows.filter(isTouchedSelfRow);
  if (touchedSelfRows.length) {
    // Existing rows minus staged removals, with staged edits already applied in place by AG Grid, plus
    // staged adds (no id). Suppressed rows are never staged — they are hidden from the grid entirely —
    // so they are appended straight from the server's own read, unchanged, or this PUT would delete them.
    const survivingSelfRows = displayRows.filter((row) => !isSkyward(row) && !row._removed);
    const payload = [...survivingSelfRows, ...suppressedRows].map((row) => toDetailPayload(row, applicationId));

    try {
      await api.putApplicationLossHistory(applicationId, payload);
    } catch (error) {
      touchedSelfRows.forEach((row) => failures.push({ key: row._key, label: rowLabel(row), error }));
    }
  }

  return { failures };
};
